import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { workforceAPI, employeeAPI } from '../lib/api';
import { formatINR, formatDate, todayISO, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import DatePicker from '../components/DatePicker';
import { toast } from 'sonner';
import {
  Activity,
  Loader2,
  RefreshCw,
  WifiOff,
  Settings as SettingsIcon,
  AlertTriangle,
  Clock,
  Gauge,
  Users,
  MonitorSmartphone,
  Link2,
  ChevronRight,
  Wifi,
  X,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const fmtHrs = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0 && m === 0) return '—';
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

// "minutes ago" → human label, for agent last-contact freshness
const agoLabel = (m) => {
  if (m == null) return 'never reported';
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m ago` : `${h}h ago`;
};

// Tiny per-employee agent liveness line. A long gap means the agent stopped
// reporting (crashed / PC asleep / offline) — so the hours shown are incomplete,
// NOT that the person isn't working.
function AgentStatus({ status, minutes, withinHours }) {
  // Live → green, always. Outside work hours a non-reporting agent is NORMAL
  // (people went home), so show it calmly in grey — never a red alarm. Only during
  // work hours does a gap mean a real problem.
  if (status === 'live') {
    return (
      <span className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-400">
        <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-green-500" />reporting live
      </span>
    );
  }
  if (!withinHours) {
    return (
      <span className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-400">
        <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-gray-300" />off · {agoLabel(minutes)}
      </span>
    );
  }
  const map = {
    lagging: { dot: 'bg-amber-500', text: 'text-amber-600', label: agoLabel(minutes) },
    offline: { dot: 'bg-red-500',   text: 'text-red-600',   label: `stopped · ${agoLabel(minutes)}` },
    unknown: { dot: 'bg-gray-300',  text: 'text-gray-400',  label: 'never reported' },
  };
  const s = map[status] || map.unknown;
  return (
    <span className={`flex items-center gap-1.5 mt-0.5 text-[11px] ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  );
}

// Display window for the per-day timeline (IST hours, inclusive)
const TIMELINE_HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 08:00–21:00

// Admin-only. Gate before the dashboard mounts so non-admins never hit the API.
export default function Workforce() {
  const { can } = useAuth();
  if (!can('workforce')) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <Activity size={40} className="mx-auto text-gray-400 mb-4" />
        <h1 className="text-xl font-semibold mb-2">Admins only</h1>
        <p className="text-sm text-gray-500">Workforce monitoring is visible to admins only.</p>
      </div>
    );
  }
  return <WorkforceDashboard />;
}

function WorkforceDashboard() {
  const [status, setStatus] = useState(null); // {configured, online}
  const [statusLoading, setStatusLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState('overview');
  // Clicking an employee in Overview jumps to By Person for that person, carrying
  // the date range selected in Overview. nonce lets a repeat click re-trigger sync.
  const [personFocus, setPersonFocus] = useState(null);
  const openPerson = useCallback((employeeId, from, to) => {
    setPersonFocus((prev) => ({ employeeId, from, to, nonce: (prev?.nonce || 0) + 1 }));
    setTab('person');
  }, []);

  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const [stRes, empRes] = await Promise.all([
        workforceAPI.status(),
        employeeAPI.getAll(),
      ]);
      setStatus(stRes.data);
      setEmployees(empRes.data || []);
    } catch {
      setStatus({ configured: false, online: false });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center space-y-4">
        <Activity size={40} className="mx-auto text-gray-400" />
        <h1 className="text-xl font-semibold">Workforce Monitor not configured</h1>
        <p className="text-sm text-gray-500">
          Add the ingest service URL and read key in Settings, then come back here.
        </p>
        <Link to="/settings">
          <Button variant="outline"><SettingsIcon className="w-4 h-4 mr-2" /> Open Settings</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Workforce</h1>
          <p className="text-gray-500 mt-1">Activity from desktop agents, joined with PriceIntel costs. All times IST.</p>
        </div>
        {!status.online && (
          <Button variant="outline" size="sm" onClick={checkStatus}>
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        )}
      </div>

      {!status.online && (
        <div className="flex items-center gap-3 p-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span><b>Monitor offline.</b> The ingest service isn't reachable right now — data below may fail to load. Agents keep buffering locally and will catch up.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="person">By Person</TabsTrigger>
          <TabsTrigger value="department">By Department</TabsTrigger>
          <TabsTrigger value="machines">Machines</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab onAssign={() => setTab('machines')} onOpenPerson={openPerson} />
        </TabsContent>
        <TabsContent value="person" className="mt-6">
          <PersonTab employees={employees} focus={personFocus} />
        </TabsContent>
        <TabsContent value="department" className="mt-6">
          <DepartmentTab />
        </TabsContent>
        <TabsContent value="machines" className="mt-6">
          <MachinesTab employees={employees} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================ OVERVIEW ============================ */

const OV_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
];
const ovIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ovQuery = (key, customDate) => {
  const t = new Date();
  if (key === 'day') return { date: customDate, label: customDate };
  if (key === '7d') { const s = new Date(t); s.setDate(t.getDate() - 6); return { date_start: ovIso(s), date_end: ovIso(t), label: 'Last 7 days' }; }
  if (key === '30d') { const s = new Date(t); s.setDate(t.getDate() - 29); return { date_start: ovIso(s), date_end: ovIso(t), label: 'Last 30 days' }; }
  if (key === 'this_month') return { date_start: ovIso(new Date(t.getFullYear(), t.getMonth(), 1)), date_end: ovIso(t), label: 'This month' };
  if (key === 'last_month') { const s = new Date(t.getFullYear(), t.getMonth() - 1, 1); const e = new Date(t.getFullYear(), t.getMonth(), 0); return { date_start: ovIso(s), date_end: ovIso(e), label: 'Last month' }; }
  return { date: todayISO(), label: 'Today' };
};

function OverviewTab({ onAssign, onOpenPerson }) {
  const [preset, setPreset] = useState('today');
  const [customDate, setCustomDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const q = preset === 'day' ? ovQuery('day', customDate) : ovQuery(preset);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { label, ...params } = q;
      const res = await workforceAPI.overview(params);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load'));
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.date, q.date_start, q.date_end]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis;
  // Range carried into By Person when a row is clicked (single day → from == to).
  const personRange = q.date ? { from: q.date, to: q.date } : { from: q.date_start, to: q.date_end };
  // Only treat "not reporting" as a problem during work hours (else it's just end of day).
  const withinHours = !!data?.within_work_hours_now;

  return (
    <div className="space-y-4">
      {/* Quick date-range presets (clickable) + a custom day picker */}
      <div className="flex flex-wrap items-center gap-2">
        {OV_PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${preset === p.key ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-2 sm:ml-auto">
          <span className="text-xs text-gray-400">or a day</span>
          <div className="w-40">
            <DatePicker value={customDate} onChange={(d) => { setCustomDate(d); setPreset('day'); }} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        {data?.work_policy ? (
          <p className="text-[10px] text-gray-300">Target {fmtHrs(data.work_policy.expected_active_seconds_per_day)}/day · Mon–Sat · outside-hours activity not counted</p>
        ) : <span />}
        {data?.rows?.length > 0 && (
          <p className="text-[10px] text-gray-400 whitespace-nowrap">Tip: click a row to open that person's report →</p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : error ? (
        <OfflineNote message={error} onRetry={load} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiCard icon={Clock} label="Total active" value={fmtHrs(k.total_active_seconds)} />
            <KpiCard icon={Activity} label="Avg active / person" value={fmtHrs(k.avg_active_seconds)} />
            <KpiCard
              icon={Gauge}
              label="Team utilisation"
              value={k.avg_utilization_pct != null ? `${k.avg_utilization_pct}%` : '—'}
              sub={k.is_work_day ? `of ${fmtHrs(k.expected_active_seconds)} expected/head` : 'rest day'}
            />
            <KpiCard icon={Clock} label="Total idle" value={fmtHrs(k.total_idle_seconds)} muted />
            <KpiCard icon={Users} label="Tracked" value={`${k.tracked_headcount}`} sub={k.unmapped_machines > 0 ? `+${k.unmapped_machines} unmapped` : (data?.is_range ? q.label : '')} />
            <KpiCard
              icon={Wifi}
              label="Reporting now"
              value={`${k.agents_live ?? 0}/${k.tracked_headcount}`}
              sub={!withinHours ? 'outside work hours' : (k.agents_offline > 0 ? `${k.agents_offline} stopped reporting` : 'all agents live')}
              muted={withinHours && k.agents_offline > 0}
            />
          </div>

          {withinHours && k.agents_offline > 0 && (
            <div className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-sm">
              <WifiOff className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <b>{k.agents_offline} agent{k.agents_offline > 1 ? 's' : ''} stopped reporting.</b> Those people may be working — their PC's agent crashed or went offline, so today's hours below are <b>incomplete</b> for them (look for the red dot). They resume on next login; the agent update fixes the mid-day drop-outs.
              </span>
            </div>
          )}
          {!withinHours && (
            <div className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-gray-50 text-gray-500 text-sm">
              <Clock className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Outside work hours (Mon–Sat 10:00–19:00) — agents idle when people are off the clock is normal, not a fault. Live status resumes during work hours.</span>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              {data.rows.length === 0 && data.unmapped.length === 0 ? (
                <div className="py-16 text-center text-gray-400 text-sm">
                  No activity recorded for {q.label}. Agents upload every few minutes — if this is a workday, check that agents are running.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[840px]">
                    <thead className="border-b">
                      <tr className="text-left text-gray-500">
                        <th className="py-3 px-4 font-medium">Employee</th>
                        <th className="py-3 px-4 font-medium">Department</th>
                        <th className="py-3 px-4 font-medium text-right">Active</th>
                        <th className="py-3 px-4 font-medium text-right">Idle</th>
                        <th className="py-3 px-4 font-medium text-right">Productive</th>
                        <th className="py-3 px-4 font-medium text-right">Util</th>
                        <th className="py-3 px-4 font-medium">Top app</th>
                        <th className="py-3 px-4 font-medium">Top title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr
                          key={r.employee_id}
                          onClick={() => onOpenPerson?.(r.employee_id, personRange.from, personRange.to)}
                          title={`Open ${r.employee_name}'s report for ${q.label}`}
                          className={`border-b last:border-b-0 cursor-pointer ${withinHours && r.agent_status === 'offline' ? 'bg-red-50/50 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="py-3 px-4 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                              {r.employee_name}
                            </span>
                            <AgentStatus status={r.agent_status} minutes={r.last_seen_minutes} withinHours={withinHours} />
                          </td>
                          <td className="py-3 px-4 text-gray-600">{r.department || '—'}</td>
                          <td className="py-3 px-4 text-right font-mono">{fmtHrs(r.active_seconds)}</td>
                          <td className="py-3 px-4 text-right font-mono text-gray-500">{fmtHrs(r.idle_seconds)}</td>
                          <td className="py-3 px-4 text-right font-mono">
                            {r.productive_pct != null
                              ? <span className={r.productive_pct >= 70 ? 'text-green-700' : r.productive_pct >= 40 ? 'text-amber-600' : 'text-red-600'}>{r.productive_pct}%</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-3 px-4 text-right font-mono">
                            {r.utilization_pct != null
                              ? <span className={r.utilization_pct >= 85 ? 'text-green-700' : r.utilization_pct >= 60 ? 'text-amber-600' : 'text-red-600'}>{r.utilization_pct}%</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-3 px-4 text-gray-600 max-w-[180px] truncate" title={r.top_app}>{r.top_app || '—'}</td>
                          <td className="py-3 px-4 text-gray-500 max-w-[260px] truncate" title={r.top_title}>{r.top_title || '—'}</td>
                        </tr>
                      ))}
                      {data.unmapped.map((u) => (
                        <tr key={u.machine_id} className="border-b last:border-b-0 bg-amber-50/40 hover:bg-amber-50">
                          <td className="py-3 px-4">
                            <span className="font-mono text-xs">{u.machine_id}</span>
                            <button onClick={onAssign} className="ml-2 text-xs underline text-amber-800">
                              Unmapped — assign
                            </button>
                          </td>
                          <td className="py-3 px-4 text-gray-400">—</td>
                          <td className="py-3 px-4 text-right font-mono">{fmtHrs(u.active_seconds)}</td>
                          <td className="py-3 px-4 text-right font-mono text-gray-500">{fmtHrs(u.idle_seconds)}</td>
                          <td className="py-3 px-4 text-gray-400" colSpan={4}>Map this machine to an employee to include it above</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/* ============================ BY PERSON ============================ */

function PersonTab({ employees, focus }) {
  const [employeeId, setEmployeeId] = useState('');
  const [from, setFrom] = useState(daysAgoISO(6));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Opened via an Overview row click: adopt that employee and the range chosen there.
  useEffect(() => {
    if (!focus) return;
    setEmployeeId(focus.employeeId);
    if (focus.from) setFrom(focus.from);
    if (focus.to) setTo(focus.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce]);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError('');
    try {
      const res = await workforceAPI.person(employeeId, from, to);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, from, to]);

  useEffect(() => { load(); }, [load]);

  const cost = data?.cost;
  const totals = data?.totals;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="w-full sm:w-64">
          <Label className="text-xs text-gray-500">Employee</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger><SelectValue placeholder="Pick an employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} · {e.department}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <Label className="text-xs text-gray-500">From</Label>
          <DatePicker value={from} onChange={setFrom} />
        </div>
        <div className="w-full sm:w-44">
          <Label className="text-xs text-gray-500">To</Label>
          <DatePicker value={to} onChange={setTo} />
        </div>
      </div>

      {!employeeId ? (
        <div className="py-16 text-center text-gray-400 text-sm">Pick an employee to see their activity and cost context.</div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : error ? (
        <OfflineNote message={error} onRetry={load} />
      ) : !data ? null : data.machine_ids.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          No machine is mapped to <b>{data.employee.name}</b> yet — map one in the Machines tab.
        </div>
      ) : (
        <>
          {/* Cost context — why this lives in PriceIntel */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Clock} label="Active hours" value={fmtHrs(totals.active_seconds)} sub={`${totals.tracked_days}/${totals.calendar_days} days${totals.after_hours_active_seconds > 0 ? ` · ${fmtHrs(totals.after_hours_active_seconds)} after-hours` : ''}`} />
            <KpiCard icon={Activity} label="Period cost" value={formatINR(cost.period_cost)} sub={`${formatINR(data.employee.per_day_cost)}/day × ${totals.calendar_days}d`} />
            <KpiCard
              icon={Activity}
              label="Cost / active hour"
              value={cost.cost_per_active_hour != null ? formatINR(cost.cost_per_active_hour) : '—'}
            />
            <Card className={cost.low_utilisation ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  {cost.low_utilisation && <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
                  Utilisation
                </p>
                <p className={`text-xl font-bold font-mono mt-1 ${cost.low_utilisation ? 'text-red-700' : ''}`}>
                  {totals.utilization_pct != null ? `${totals.utilization_pct}%` : '—'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {cost.avg_active_hours_per_tracked_day}h/day avg
                  {totals.paid_holiday_days_considered > 0 ? ` · ${totals.paid_holiday_days_considered} paid hol.` : ''}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Productivity + what they worked on */}
          {data.productivity && (data.productivity.productive || data.productivity.neutral || data.productivity.unproductive) ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Focus</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold">{data.productive_pct != null ? data.productive_pct + '%' : '—'}</span>
                    <span className="text-sm text-gray-500 mb-1">productive</span>
                  </div>
                  <ProductivityBar prod={data.productivity} />
                  <div className="flex flex-col gap-1.5 mt-3 text-xs text-gray-600">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#16a34a' }} /> Productive · {fmtHrs(data.productivity.productive || 0)}</span>
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#9ca3af' }} /> Neutral · {fmtHrs(data.productivity.neutral || 0)}</span>
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#dc2626' }} /> Entertainment · {fmtHrs(data.productivity.unproductive || 0)}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="text-base">What they worked on</CardTitle></CardHeader>
                <CardContent><CategoryBars categories={data.categories} /></CardContent>
              </Card>
            </div>
          ) : null}

          {/* Day-by-day timeline 08:00–21:00 IST */}
          <Card>
            <CardHeader><CardTitle className="text-base">Daily timeline <span className="text-xs font-normal text-gray-400 ml-1">08:00 – 21:00 IST</span></CardTitle></CardHeader>
            <CardContent>
              {data.days.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No activity in this range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="border-separate" style={{ borderSpacing: '2px' }}>
                    <thead>
                      <tr>
                        <th className="text-left text-xs text-gray-400 font-normal pr-3 pb-1">Day</th>
                        {TIMELINE_HOURS.map((h) => (
                          <th key={h} className="text-[10px] text-gray-400 font-normal pb-1 min-w-[34px]">{String(h).padStart(2, '0')}</th>
                        ))}
                        <th className="text-xs text-gray-400 font-normal pl-3 pb-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.days.map((d) => (
                        <tr key={d.date}>
                          <td className="text-xs text-gray-600 pr-3 whitespace-nowrap">{formatDate(d.date)}</td>
                          {TIMELINE_HOURS.map((h) => {
                            const sec = d.hourly[h] || 0;
                            const frac = Math.min(sec / 3600, 1);
                            return (
                              <td
                                key={h}
                                title={`${String(h).padStart(2, '0')}:00 — ${fmtHrs(sec)} active`}
                                className="h-7 rounded-sm border border-gray-100"
                                style={sec > 0 ? { backgroundColor: `rgba(0,0,0,${(0.08 + 0.92 * frac).toFixed(2)})` } : { backgroundColor: '#fafafa' }}
                              />
                            );
                          })}
                          <td className="text-xs font-mono text-gray-700 pl-3 whitespace-nowrap">{fmtHrs(d.active_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sites, documents, apps, titles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {data.top_sites?.length > 0 && (
              <TopList title="Top sites & tools" rows={data.top_sites.map((x) => ({ label: x.site, seconds: x.seconds }))} />
            )}
            {data.top_documents?.length > 0 && (
              <TopList title="Top documents / projects" rows={data.top_documents.map((x) => ({ label: x.document, seconds: x.seconds }))} />
            )}
            <TopList title="Top apps" rows={data.top_apps.map((a) => ({ label: a.app, seconds: a.seconds }))} />
            <TopList title="Top window titles" rows={data.top_titles.map((t) => ({ label: t.title, seconds: t.seconds }))} />
          </div>

          {data.clients?.length > 0 && (
            <TopList title="Time by client" rows={data.clients.map((x) => ({ label: x.client, seconds: x.seconds }))} />
          )}
        </>
      )}
    </div>
  );
}

const CATEGORY_COLORS = {
  'Design': '#7c3aed',
  'Video': '#db2777',
  'Social & Ads': '#2563eb',
  'Communication': '#0891b2',
  'Docs & Admin': '#65a30d',
  'Research & Web': '#9ca3af',
  'Entertainment': '#dc2626',
  'Other': '#d1d5db',
};

function ProductivityBar({ prod }) {
  const p = prod.productive || 0, n = prod.neutral || 0, u = prod.unproductive || 0;
  const tot = p + n + u;
  if (!tot) return null;
  const seg = (v, c) => (v > 0 ? <div style={{ width: `${(v / tot) * 100}%`, background: c }} className="h-full" /> : null);
  return (
    <div className="flex h-3 w-full rounded-full overflow-hidden mt-3 bg-gray-100">
      {seg(p, '#16a34a')}{seg(n, '#9ca3af')}{seg(u, '#dc2626')}
    </div>
  );
}

function CategoryBars({ categories }) {
  if (!categories || categories.length === 0) return <p className="text-sm text-gray-400 py-4 text-center">No category data yet</p>;
  const max = categories[0]?.seconds || 1;
  return (
    <div className="space-y-2.5">
      {categories.map((c) => (
        <div key={c.category} className="flex items-center gap-3">
          <div className="w-32 text-sm text-gray-700 shrink-0 truncate" title={c.category}>{c.category}</div>
          <div className="flex-1 bg-gray-100 rounded-full h-2.5">
            <div className="h-2.5 rounded-full" style={{ width: `${Math.max(4, (c.seconds / max) * 100)}%`, background: CATEGORY_COLORS[c.category] || '#888' }} />
          </div>
          <div className="text-xs font-mono text-gray-600 w-16 text-right shrink-0">{fmtHrs(c.seconds)}</div>
        </div>
      ))}
    </div>
  );
}

function TopList({ title, rows }) {
  const max = rows.length ? rows[0].seconds : 0;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No data</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate" title={r.label}>{r.label}</div>
                  <div className="h-1.5 bg-gray-100 rounded-full mt-1">
                    <div className="h-1.5 bg-black rounded-full" style={{ width: `${max ? Math.max(4, (r.seconds / max) * 100) : 0}%` }} />
                  </div>
                </div>
                <div className="text-xs font-mono text-gray-600 w-16 text-right shrink-0">{fmtHrs(r.seconds)}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================ BY DEPARTMENT ============================ */

function DepartmentTab() {
  const [from, setFrom] = useState(daysAgoISO(6));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workforceAPI.departments(from, to);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const chartData = (data?.departments || []).map((d) => ({
    name: d.department.length > 18 ? d.department.slice(0, 18) + '…' : d.department,
    fullName: d.department,
    hours: Math.round((d.active_seconds / 3600) * 10) / 10,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="w-full sm:w-44">
          <Label className="text-xs text-gray-500">From</Label>
          <DatePicker value={from} onChange={setFrom} />
        </div>
        <div className="w-full sm:w-44">
          <Label className="text-xs text-gray-500">To</Label>
          <DatePicker value={to} onChange={setTo} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : error ? (
        <OfflineNote message={error} onRetry={load} />
      ) : !data || data.departments.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm">No tracked activity in this range. Map machines to employees first.</div>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle className="text-base">Active hours by department</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 48)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 24, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis type="number" unit="h" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#444' }} interval={0} />
                  <Tooltip
                    formatter={(v) => [`${v}h`, 'Active']}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
                  />
                  <Bar dataKey="hours" fill="#000000" radius={[0, 4, 4, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="border-b">
                    <tr className="text-left text-gray-500">
                      <th className="py-3 px-4 font-medium">Department</th>
                      <th className="py-3 px-4 font-medium text-right">Active</th>
                      <th className="py-3 px-4 font-medium text-right">Avg / person</th>
                      <th className="py-3 px-4 font-medium text-right">Tracked</th>
                      <th className="py-3 px-4 font-medium text-right">Dept cost ({data.calendar_days}d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.departments.map((d) => (
                      <tr key={d.department} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{d.department}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmtHrs(d.active_seconds)}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmtHrs(d.avg_active_seconds)}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{d.tracked_headcount} / {d.total_headcount}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatINR(d.department_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 px-4 py-3 border-t">
                Dept cost = sum of per-day cost of <i>tracked</i> employees × {data.calendar_days} calendar days. Untracked teammates aren't counted.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/* ============================ MACHINES ============================ */

function MachinesTab({ employees }) {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState('');
  const [autoMapping, setAutoMapping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workforceAPI.machines();
      setMachines(res.data || []);
    } catch (err) {
      setError(apiError(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAutoMap = async () => {
    setAutoMapping(true);
    try {
      const res = await workforceAPI.autoMap();
      const d = res.data;
      if (d.mapped_count > 0) {
        toast.success(`Auto-mapped ${d.mapped_count} machine${d.mapped_count === 1 ? '' : 's'} by M365 email`);
      } else {
        const reason = d.skipped_no_account_reported > 0
          ? 'no machine is reporting an M365 account yet'
          : 'no reported accounts matched an employee email';
        toast.info(`Nothing to map — ${reason}`);
      }
      load();
    } catch (err) {
      toast.error(apiError(err, 'Auto-map failed'));
    } finally {
      setAutoMapping(false);
    }
  };

  const anySuggestions = machines.some((m) => m.suggested_employee_id);

  const assign = async (machineId, employeeId) => {
    setSavingId(machineId);
    try {
      await workforceAPI.setMachineMap(machineId, employeeId);
      toast.success('Machine mapped');
      load();
    } catch (err) {
      toast.error(apiError(err, 'Failed to map'));
    } finally {
      setSavingId('');
    }
  };

  const clear = async (machineId) => {
    setSavingId(machineId);
    try {
      await workforceAPI.deleteMachineMap(machineId);
      toast.success('Mapping removed');
      load();
    } catch (err) {
      toast.error(apiError(err, 'Failed to remove'));
    } finally {
      setSavingId('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MonitorSmartphone className="w-4 h-4" /> Machine → employee mapping
          </CardTitle>
          <Button
            type="button"
            variant={anySuggestions ? 'default' : 'outline'}
            size="sm"
            className={anySuggestions ? 'bg-black hover:bg-black/90' : ''}
            onClick={runAutoMap}
            disabled={autoMapping}
          >
            {autoMapping ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
            Auto-map by M365 email
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : error ? (
          <div className="p-6"><OfflineNote message={error} onRetry={load} /></div>
        ) : machines.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            No machines have reported yet. Once a desktop agent uploads its first batch, it appears here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="border-b">
                <tr className="text-left text-gray-500">
                  <th className="py-3 px-4 font-medium">Machine ID</th>
                  <th className="py-3 px-4 font-medium">M365 account</th>
                  <th className="py-3 px-4 font-medium">Last seen</th>
                  <th className="py-3 px-4 font-medium">Mapped employee</th>
                  <th className="py-3 px-4 w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => (
                  <tr key={m.machine_id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-4 text-xs">
                      <div className="font-mono">{m.hostname || m.machine_id}</div>
                      {m.hostname && m.hostname !== m.machine_id && (
                        <div className="text-gray-400 truncate max-w-[200px]" title={m.machine_id}>{m.machine_id}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {m.user_upn || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-xs">
                      {m.last_seen ? new Date(m.last_seen).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {savingId === m.machine_id && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                        <Select
                          value={m.employee_id || ''}
                          onValueChange={(v) => assign(m.machine_id, v)}
                          disabled={savingId === m.machine_id}
                        >
                          <SelectTrigger className="w-[260px]">
                            <SelectValue placeholder={
                              <span className="flex items-center gap-1 text-amber-700">
                                <Link2 className="w-3.5 h-3.5" /> Unmapped — pick employee
                              </span>
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((e) => (
                              <SelectItem key={e.id} value={e.id}>{e.name} · {e.department}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!m.employee_id && m.suggested_employee_id ? (
                          <button
                            onClick={() => assign(m.machine_id, m.suggested_employee_id)}
                            className="text-xs underline text-blue-700 whitespace-nowrap"
                            title={`Matches ${m.user_upn}`}
                          >
                            ✓ {m.suggested_employee_name}?
                          </button>
                        ) : !m.employee_id ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">unmapped</Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {m.employee_id && (
                        <Button
                          variant="ghost" size="sm" title="Remove mapping"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => clear(m.machine_id)}
                          disabled={savingId === m.machine_id}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================ SHARED ============================ */

function KpiCard({ icon: Icon, label, value, sub, muted }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
          <Icon className="w-3.5 h-3.5" /> {label}
        </p>
        <p className={`text-xl font-bold font-mono mt-1 ${muted ? 'text-gray-400' : ''}`}>{value}</p>
        {sub ? <p className="text-xs text-gray-400 mt-1">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function OfflineNote({ message, onRetry }) {
  return (
    <div className="py-12 text-center space-y-3">
      <WifiOff className="w-8 h-8 mx-auto text-gray-300" />
      <p className="text-sm text-gray-500">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="w-4 h-4 mr-1" /> Retry
      </Button>
    </div>
  );
}
